import { useCallback, useMemo, useState } from 'react';
import { BudgetScenarioPartnerLocationOption, BudgetScenarioWorkingUnitOption } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import {
    DraftIssue,
    DraftUpdater,
    ScenarioShiftDraftRow,
    patchDraftMonthValue,
    patchTrainingRowField,
    removeDraftRow,
    shiftRowHasInput,
    shiftTrainingCost,
} from './scenarioDraft';
import {
    formatCurrency,
    formatDecimal,
    formatPercent,
    SCENARIO_ENTITY_COL_MIN_WIDTH,
    SCENARIO_ROW_LABEL_COL_WIDTH,
} from './helpers';
import ScenarioRowGrid, { ScenarioRowColumn, ScenarioRowOption } from './ScenarioRowGrid';

interface ScenarioShiftGridProps {
    months: string[];
    /** Read for RENDERING only: an edit is derived from the `previous` an updater is handed, never from this
     *  prop (see DraftUpdater) — which matters most here, where a patch reads the row it is patching. */
    rows: ScenarioShiftDraftRow[];
    onChange: (update: DraftUpdater<ScenarioShiftDraftRow[]>) => void;
    issues: DraftIssue[];
    availablePartnerLocations: BudgetScenarioPartnerLocationOption[];
    /** Each carries the locations its working unit runs at — the row's Von/Nach choices (CALL-783). */
    availableWorkingUnits: BudgetScenarioWorkingUnitOption[];
    /** Appends a blank row. Owned by the page rather than built here, because the persistent "add" button
     *  sits in the section heading — the one row both views of the section share — while this grid only
     *  offers it again inside its empty state (CALL-784). */
    onAddRow: () => void;
    isLoading: boolean;
    canEdit: boolean;
    /** National public-holiday dates ('YYYY-MM-DD') the Trainingstage prefill subtracts besides weekends
     *  (CALL-801) — the same set the backend's training allocation uses. Undefined while the page is still
     *  loading them, which suppresses the prefill rather than counting holidays as working days. */
    holidays?: ReadonlySet<string>;
}

// A persisted value the current options no longer contain is APPENDED rather than dropped: ag-grid renders a
// value that is not among the options as a blank cell, and the row would then silently lose it on the next
// edit. Same guard as ScenarioDriverGrid, here applied to all three of shift's dropdowns.
const withCurrentValue = (
    options: ScenarioRowOption[],
    current: number | '',
    fallback: (current: number) => ScenarioRowOption
): ScenarioRowOption[] =>
    current === '' || options.some((option) => option.id === current) ? options : [...options, fallback(current)];

// Input grid for a Verschiebung scenario: one row per Von→Nach move of a Working Unit's volume, with its
// training window, training inputs and a monthly ramp (absolute percent of the Von location's volume
// shifted each month). Only `Trainingskosten` is derived; `Ende Training` is entered (CALL-783 — the
// weekday walk that used to derive it ignores public holidays and travel time, and the board shows Ende as
// a real date column beside Trainingstage). Inputs only — the calculated cost impact (savings − training)
// is the section's other view.
//
// `Anteil %` is gone with the same change: it was informational, no calc ever read it, and the monthly ramp
// already carries the percent really shifted — so it could only disagree with the figures beside it.
//
// Everything mechanical lives in ScenarioRowGrid (CALL-783); this file owns the column list, the per-row
// option sets and the edit handlers. Edits report a new draft upward; the page's single Save commits them.
//
// Shift keeps BOTH an add-row action and its trash, unlike Driver: shift rows have no uniqueness index, so
// several may legitimately share a working unit (splitting one location's volume across two targets). The
// form's multiselect creates a working unit's FIRST row; every further target comes from "add", which is why
// the selection is not a bijection with the rows.
const ScenarioShiftGrid = ({
    months,
    rows,
    onChange,
    issues,
    availablePartnerLocations,
    availableWorkingUnits,
    onAddRow,
    isLoading,
    canEdit,
    holidays,
}: ScenarioShiftGridProps) => {
    const { t } = useTranslation('budget');
    // Removing a row that holds values discards them on the next save — confirm first. A still-blank row
    // can go without friction.
    const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);

    const locationOption = useCallback(
        (locationId: number): ScenarioRowOption => ({
            id: locationId,
            label:
                availablePartnerLocations.find((option) => option.locationId === locationId)?.label ?? `#${locationId}`,
        }),
        [availablePartnerLocations]
    );

    // Which locations each working unit may be shifted between: the ones the bucket has a (non-flat-fee)
    // billing config for it at, as reported by the shift GET. A pair without a config has no rate on that
    // side, so shiftCalc books zero savings — offering all bucket locations let the user build a move that
    // silently computed to nothing (or, for Nach, to training cost alone). Label-sorted like the
    // multiselects; relaxing this to "move to a brand-new site" is one line here.
    const locationsByWorkingUnit = useMemo(() => {
        const map = new Map<number, ScenarioRowOption[]>();
        for (const unit of availableWorkingUnits) {
            map.set(
                unit.workingUnitId,
                unit.locationIds.map(locationOption).sort((a, b) => a.label.localeCompare(b.label))
            );
        }
        return map;
    }, [availableWorkingUnits, locationOption]);

    const workingUnitOptionsFor = useCallback(
        (row: ScenarioShiftDraftRow): ScenarioRowOption[] => {
            const options = availableWorkingUnits.map((option) => ({
                id: option.workingUnitId,
                label: option.label,
            }));
            return withCurrentValue(options, row.workingUnitId, (current) => ({
                id: current,
                label: row.workingUnitLabel ?? `#${current}`,
            }));
        },
        [availableWorkingUnits]
    );

    // Von and Nach both read the ROW's working unit, which is why they are functions of the row. With no unit
    // picked yet there is deliberately nothing to offer — the scope note beside the multiselect says the unit
    // comes first, and an empty list is honest about a question that cannot be answered yet.
    const locationOptionsFor = useCallback(
        (row: ScenarioShiftDraftRow, current: number | ''): ScenarioRowOption[] => {
            const options = row.workingUnitId === '' ? [] : (locationsByWorkingUnit.get(row.workingUnitId) ?? []);
            return withCurrentValue(options, current, locationOption);
        },
        [locationsByWorkingUnit, locationOption]
    );

    // Board order (screen_014): identity, the move, the training window, then the money, then the ramp.
    //
    // Widths follow the board's proportions, and since CALL-784 its arithmetic too. Eleven leading columns at
    // the old sizes totalled 1530px, so in a 1400px viewport the month block began past the right edge —
    // ZERO of it visible on the one screen whose subject IS the monthly ramp. Every NUMERIC column is sized
    // for its CELL rather than for its caption (headers truncate with a tooltip).
    //
    // The three LABEL columns are the exception, and since CALL-783 they are sized by legibility rather than
    // by what is left over: the rows are double height so a label wraps, and a column must fit its longest
    // single WORD or the wrap breaks mid-word. That is what puts Working Unit at 160 and Von/Nach at 115 —
    // the reasoning and the measurements live on SCENARIO_ROW_LABEL_COL_WIDTH.
    //
    // What that costs: the nine scrolling leading columns total 794px, and how many months that leaves
    // depends on the NAV DRAWER as much as on the window, which is why every figure here names its
    // condition — an earlier version of this comment gave one number and read as if it were the only one.
    // At a 1400px window with the drawer COLLAPSED to its icon rail the shell is 1284px, so the centre
    // viewport is 1084px (the pinned 160 and the pinned 44px trash sit outside it) and 290px is left —
    // THREE 80px month columns with 50px of a fourth. Expanded, the drawer takes 240px of the same window:
    // a 916px centre viewport, 122px left, ONE month and part of a second.
    //
    // Dropping `Anteil %` paid for 70 of the 100px the wider labels cost, so the collapsed-drawer month
    // count is where it was before CALL-783 while the labels became readable; the remaining months scroll
    // behind the pinned trash, which App.css gives a shadow so it reads as an edge. Widening the
    // identifying columns beating the month count is the client's own call.
    //
    // 70 is the floor, not a choice: the shared AgGrid wrapper defaults every column to `minWidth: 70`, and
    // a minWidth above the width wins — anything smaller here would render at 70 anyway and only make the
    // declared numbers lie.
    const columns = useMemo<ScenarioRowColumn<ScenarioShiftDraftRow>[]>(
        () => [
            {
                kind: 'select',
                key: 'workingUnitId',
                headerName: t('field.workingUnit'),
                // The shared identifier width, so the input⇄impact toggle does not shift the table sideways.
                // Every real working unit wraps into two lines here, including the board's longest
                // ("Blau Care Postpaid - Inbound Voice - Alle"); the tooltip carries the full name anyway.
                width: SCENARIO_ENTITY_COL_MIN_WIDTH,
                pinned: 'left',
                options: workingUnitOptionsFor,
            },
            {
                kind: 'select',
                key: 'fromLocationId',
                headerName: t('field.shiftFrom'),
                // A partner–location label ("Concentrix – Marrakesch", 152px on one line) takes both of the
                // wrapped lines; the width is set by its longest WORD, so the break lands at the dash.
                width: SCENARIO_ROW_LABEL_COL_WIDTH,
                options: (row) => locationOptionsFor(row, row.fromLocationId),
            },
            {
                kind: 'select',
                key: 'toLocationId',
                headerName: t('field.shiftTo'),
                width: SCENARIO_ROW_LABEL_COL_WIDTH,
                options: (row) => locationOptionsFor(row, row.toLocationId),
            },
            // Both ends of the training window are now entered: Ende is what the training cost is spread
            // over, and the client picks it because the weekday walk it replaced knows nothing about public
            // holidays or travel time (CALL-783).
            //
            // A German date is "15.08.2026" — 69px at the cell font, and 78 is the smallest column that
            // SHOWS it: the theme's 4px of padding on each side leaves a 75px column only 67px of text, so
            // it rendered "15.09.20…" and hid the very digits that distinguish one year from another. These
            // two are the one kind of cell that must never truncate — a clipped label is still recognisable
            // and carries a tooltip, a clipped date is a different date.
            { kind: 'date', key: 'trainingStart', headerName: t('field.trainingStart'), width: 78 },
            { kind: 'date', key: 'trainingEnd', headerName: t('field.trainingEnd'), width: 78 },
            { kind: 'number', key: 'agents', headerName: t('field.agents'), width: 70, format: formatDecimal },
            {
                kind: 'number',
                key: 'trainingDays',
                headerName: t('field.trainingDays'),
                // The one numeric caption that fits in full — at 90 it lost its last letter to the ellipsis.
                width: 92,
                format: formatDecimal,
            },
            // …and it stays a plain editable number: setting Start and Ende PREFILLS it with the window's
            // working days (patchShiftRowField), but the field is the cost multiplier, so a client who trains
            // 15 of a 22-working-day window has to be able to type 15 over it. Editing it never moves Ende.
            {
                kind: 'number',
                key: 'costPerHour',
                headerName: t('field.costPerHour'),
                width: 70,
                format: formatCurrency,
            },
            {
                kind: 'number',
                key: 'trainerCost',
                headerName: t('field.trainerCost'),
                width: 84,
                format: formatCurrency,
            },
            {
                // The widest numeric column: it shows a six-figure euro total (€110.760,00 on the board).
                // The impact view shows the SAME quantity — from the calc's row-level `trainingTotal` rather
                // than from the inputs beside it — but 18px wider, because its column also carries a BOLD
                // sum across all rows in the pinned totals row (CALL-783). Nothing is bold here, so 92 is
                // still what this cell needs, and these columns do not align across the toggle anyway.
                kind: 'computed',
                field: 'trainingTotal',
                headerName: t('field.trainingTotal'),
                width: 92,
                compute: shiftTrainingCost,
                format: formatCurrency,
            },
        ],
        [t, workingUnitOptionsFor, locationOptionsFor]
    );

    // patchTrainingRowField rather than the generic patchDraftRow: two of this grid's columns are coupled —
    // Start and Ende prefill Trainingstage, one-directionally and only while nobody has typed over it. The
    // rule itself lives in scenarioDraft so it can be tested without a grid (CALL-783).
    //
    // Applied to the rows the updater is HANDED, which this grid needs more than its siblings do: the patch
    // reads the row it is patching (its Start, its Ende, its trainingDaysPrefilled flag), so taking the rows
    // from the `rows` prop meant that setting BOTH ends of the window in one gesture — two cellValueChanged
    // events in the same macrotask, no render between them — computed the Ende patch from a row whose Start was
    // still empty: no working days to prefill, and the Start itself lost to last-write-wins.
    const onFieldChange = useCallback(
        (clientKey: string, key: keyof ScenarioShiftDraftRow & string, value: number | string) => {
            onChange((previous) => patchTrainingRowField(previous, clientKey, key, value, holidays));
        },
        [onChange, holidays]
    );

    const onRemoveRow = useCallback(
        (row: ScenarioShiftDraftRow) => {
            if (!shiftRowHasInput(row)) {
                onChange((previous) => removeDraftRow(previous, row.clientKey));
                return;
            }
            setDeleteConfirmKey(row.clientKey);
        },
        [onChange]
    );

    const onConfirmDelete = useCallback(() => {
        if (deleteConfirmKey === null) return;
        onChange((previous) => removeDraftRow(previous, deleteConfirmKey));
        setDeleteConfirmKey(null);
    }, [onChange, deleteConfirmKey]);

    const rowLabel = useCallback(
        (row: ScenarioShiftDraftRow) =>
            row.workingUnitId === ''
                ? ''
                : (workingUnitOptionsFor(row).find((option) => option.id === row.workingUnitId)?.label ?? ''),
        [workingUnitOptionsFor]
    );

    return (
        <ScenarioRowGrid<ScenarioShiftDraftRow>
            months={months}
            rows={rows}
            columns={columns}
            isLoading={isLoading}
            canEdit={canEdit}
            issues={issues}
            formatMonthValue={formatPercent}
            rowLabel={rowLabel}
            onFieldChange={onFieldChange}
            onMonthValueChange={(clientKey, month, value) =>
                onChange((previous) => patchDraftMonthValue(previous, clientKey, month, value))
            }
            onAddRow={onAddRow}
            onRemoveRow={onRemoveRow}
            deleteConfirmOpen={deleteConfirmKey !== null}
            onCancelDelete={() => setDeleteConfirmKey(null)}
            onConfirmDelete={onConfirmDelete}
            labels={{
                addRow: t('scenarioDetail.addShiftRow'),
                noRows: t('scenarioDetail.noShiftRows'),
                deleteRow: t('scenarioDetail.deleteShiftRow'),
            }}
        />
    );
};

export default ScenarioShiftGrid;
